import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';

import { select, Store } from '@ngrx/store';
import { catchError, finalize, map, mergeMap, switchMap } from 'rxjs';
import { SilageSoilSegment } from 'src/app/models/silage-soil-segments.model';
import { SilageSoilService } from 'src/app/services/silage-soil.service';
import { ToastService } from 'src/app/services/toast.service';

import LabResultsActions from './actions';
import LabResultsSelectors from './selectors';

@Injectable()
export class LabResultsEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly silageSoilService = inject(SilageSoilService);
  private readonly toastService = inject(ToastService);

  loadAllLabResults$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LabResultsActions.loadAllLabResults),
      switchMap(() =>
        Object.values(SilageSoilSegment).map((segment) =>
          LabResultsActions.loadLabResults({ segment }),
        ),
      ),
    ),
  );

  loadLabResults$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LabResultsActions.loadLabResults),
      mergeMap(({ segment }) =>
        this.silageSoilService.getSilageSoilData(segment, null).pipe(
          map((response) =>
            LabResultsActions.loadedLabResults({ segment, response }),
          ),
          catchError(() => [LabResultsActions.loadLabResultsFailure({})]),
        ),
      ),
    ),
  );

  loadNutrientDefinitions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LabResultsActions.loadNutrientDefinitions),
      mergeMap(() =>
        this.silageSoilService.getNutrientDefinitions().pipe(
          map((response) =>
            LabResultsActions.loadedNutrientDefinitions({ response }),
          ),
          catchError(() => [
            LabResultsActions.loadNutrientDefinitionsFailure({}),
          ]),
        ),
      ),
    ),
  );

  infiniteLoadLabResults$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LabResultsActions.infiniteLoadLabResults),
      concatLatestFrom(() => [
        this.store.pipe(
          select(LabResultsSelectors.selectActiveSilageSoilSegment),
        ),
        this.store.pipe(select(LabResultsSelectors.selectSearchTerm)),
        this.store.pipe(select(LabResultsSelectors.selectNextPageNumber)),
      ]),
      mergeMap(([{ event }, segment, searchTerm, pageNumber]) =>
        this.silageSoilService
          .getSilageSoilData(segment, searchTerm, undefined, pageNumber)
          .pipe(
            map((response) =>
              LabResultsActions.loadedLabResults({
                segment,
                response,
                appendResults: true,
              }),
            ),
            catchError(() => [LabResultsActions.loadLabResultsFailure({})]),
            finalize(() => {
              event.target.complete();
            }),
          ),
      ),
    ),
  );

  searchLabResults$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LabResultsActions.updateSearchTerm),
      concatLatestFrom(() => [
        this.store.pipe(
          select(LabResultsSelectors.selectActiveSilageSoilSegment),
        ),
      ]),
      switchMap(([{ searchTerm }, segment]) =>
        this.silageSoilService.getSilageSoilData(segment, searchTerm).pipe(
          map((response) =>
            LabResultsActions.loadedLabResults({ segment, response }),
          ),
          catchError(() => [LabResultsActions.loadLabResultsFailure({})]),
        ),
      ),
    ),
  );

  loadLabResultsFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LabResultsActions.loadLabResultsFailure),
        map(({ message }) =>
          this.toastService.showWarning(
            message ?? 'Er is iets mis gegaan bij het ophalen van de gegevens.',
          ),
        ),
      ),
    { dispatch: false },
  );
}
